import { MASKED_VALUE } from '../constants';

type Config = Record<string, unknown>;

/** `config` with each set sensitive field replaced by `MASKED_VALUE`. */
export const maskSensitiveFields = <T extends Config>(
  config: T,
  fields: readonly string[],
): T => {
  const masked: Config = { ...config };
  for (const field of fields) {
    if (masked[field] !== undefined && masked[field] !== null) {
      masked[field] = MASKED_VALUE;
    }
  }
  return masked as T;
};

/**
 * `incoming` with each sensitive field that is masked or unset restored from
 * `existing`, so a client that only ever sees masked values can't clobber them.
 */
export const restoreSensitiveFields = <T extends Config>(
  incoming: T,
  existing: Config | undefined,
  fields: readonly string[],
): T => {
  const restored: Config = { ...incoming };
  for (const field of fields) {
    const value = restored[field];
    if (value === MASKED_VALUE || value === undefined || value === null) {
      restored[field] = existing?.[field];
    }
  }
  return restored as T;
};

/**
 * `config` without the sensitive fields the user left masked or blank (React
 * Admin sends a cleared input as `null`), so the backend keeps their stored
 * values.
 */
export const omitUnchangedSensitiveFields = <T extends Config>(
  config: T,
  fields: readonly string[],
): T => {
  const changed: Config = { ...config };
  for (const field of fields) {
    const value = changed[field];
    if (
      value === MASKED_VALUE ||
      value === null ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      delete changed[field];
    }
  }
  return changed as T;
};
